import type { DocNode } from '@atlaskit/adf-schema/doc';
import type { ADFEntity } from '@atlaskit/adf-utils/types';
import type { CreateUIAnalyticsEvent } from '@atlaskit/analytics-next/types';
import { traverse } from '@atlaskit/adf-utils/traverse/main';
import { defaultSchema } from '@atlaskit/adf-schema/schema-default';
import type { JSONDocNode } from '@atlaskit/editor-json-transformer/types';
import type { AgentCreatorType } from '@atlaskit/rovo-agent-components/common/types';
import { isForgeAgentByCreatorType } from '@atlaskit/rovo-agent-components/common/utils/is-forge-agent';
import { createGfmMarkdownTransformer } from '@atlassian/markdown-support/gfm-markdown-transformer';
import type { Payload } from '@atlaskit/rovo-triggers';
import { ROVO_POST_MESSAGE_EVENT_TYPE } from '@atlaskit/rovo-triggers/post-message-to-pubsub';

import { IdentityUserAri } from '@atlassian/ari/identity/user';
import { client } from '@atlassian/rovo-content-bridge-api/client-types';
import { getBrowserBridgeAPI } from '@atlassian/rovo-content-bridge-api/singleton';
import { ROVO_CHAT_CLIENT_ID } from '@atlassian/rovo-content-bridge-api-commands/clients/rovo-chat-client';
import { InlineInputBoxAbortMessageCommand } from '@atlassian/rovo-content-bridge-api-commands/commands/inline-input-box-abort-message-command';

import { expValEquals } from '@confluence/feature-experiments';
import { fg } from '@confluence/feature-gating';
import { cfetch } from '@confluence/network';
import { recordAgenticView } from '@confluence/conversation-assistant/entry-points/recordAgenticView.ts';

import {
	AGENT_MENTION_COMMENT_SAVED_EVENT,
	AGENT_RESOLVE_TIMEOUT_MS,
} from './agentMentionConstants.ts';
import {
	startOptimisticCommentAgentSession,
	clearOptimisticCommentAgentSession,
} from './optimisticCommentAgentSession.ts';
import { sendBackgroundAgentInvocation } from './sendBackgroundAgentInvocation.ts';
import { registerBackgroundConversationSource } from './backgroundConversationSource.ts';

/** ADF document type accepted from comment editors — covers both DocNode and JSONDocNode call sites */
export type AdfDocument = DocNode | JSONDocNode;

export type ConfluenceCommentType = 'inline' | 'footer';

export type AgentMentionPresenceContext = {
	contentType: 'page' | 'blogpost' | 'whiteboard' | 'database' | 'embed' | 'slide' | 'folder';
	pageContext: 'confluence-page-view' | 'confluence-page-edit';
};

export type AgentMentionInfo = {
	agentId: string;
	agentName: string;
	/** The mention paragraph's text, or the full comment text when inline comments are enabled. */
	promptText: string;
};

/** Requests Rovo Chat to stop the active generation for the current conversation. */
export function abortCommentAgentGeneration(conversationId: string): void {
	const bridge = getBrowserBridgeAPI();
	const senderId = client(`confluence-comment-agent-abort-${conversationId}`);
	bridge.ready(senderId);
	try {
		void bridge
			.send(InlineInputBoxAbortMessageCommand, {
				senderId,
				receiverId: ROVO_CHAT_CLIENT_ID,
				conversationId,
			})
			.result()
			.catch(() => undefined)
			.finally(() => bridge.teardown(senderId));
	} catch {
		bridge.teardown(senderId);
	}
}

type ResolvedAgent = {
	id: string;
	namedId: string;
	name: string;
	creatorType?: AgentCreatorType;
	/** OOTB agents — external_config_reference; preferred over namedId for the fixed avatar. */
	externalConfigReference?: string;
};

const MAX_TRACKED_COMMENT_INVOCATIONS = 500;
let gfmMarkdownTransformer: ReturnType<typeof createGfmMarkdownTransformer> | undefined;
// Deduplicate repeated save callbacks for a comment within this page lifetime. This bounded,
// in-memory set is not persisted session state or a guarantee across reloads.
const invokedCommentIds = new Set<string>();

function claimCommentInvocation(commentId: string): boolean {
	if (!commentId) {
		return true;
	}
	if (invokedCommentIds.has(commentId)) {
		return false;
	}
	if (invokedCommentIds.size >= MAX_TRACKED_COMMENT_INVOCATIONS) {
		const oldestCommentId = invokedCommentIds.values().next().value;
		if (oldestCommentId) {
			invokedCommentIds.delete(oldestCommentId);
		}
	}
	invokedCommentIds.add(commentId);
	return true;
}

export function clearCommentInvocationClaimsForTesting(): void {
	invokedCommentIds.clear();
}

/**
 * Cheap presence check — returns true on first matching mention without walking
 * the rest of the tree or building any intermediate objects. Use this on the
 * render path (e.g. on every keystroke for the FYI nudge) where the boolean is
 * the only thing that matters.
 *
 * Note: keyed on `userType === 'APP'`, which is only reliable for LIVE (unsaved)
 * comment ADF. The backend strips `userType` on persistence, so this returns
 * false for already-saved/copy-pasted agent mentions — acceptable for the
 * live-editor nudge. The save path (`handleAgentMentionInComment`) falls back to
 * id-resolution and does not depend on this.
 */
export function hasAgentMention(adf: AdfDocument | undefined): boolean {
	if (!adf?.content) {
		return false;
	}

	const stack: ADFEntity[] = [adf as ADFEntity];

	while (stack.length > 0) {
		const node = stack.pop();

		if (!node) {
			break;
		}

		if (
			node.type === 'mention' &&
			node.attrs?.userType === 'APP' &&
			typeof node.attrs?.id === 'string' &&
			node.attrs.id !== ''
		) {
			return true;
		}

		if (Array.isArray(node.content)) {
			// eslint-disable-next-line @atlassian/perf-linting/no-quadratic-complexity -- ADF trees are shallow (typically <10 levels); linear in total node count
			for (const child of node.content) {
				if (child) {
					stack.push(child);
				}
			}
		}
	}

	return false;
}

/**
 * Builds an `AgentMentionInfo` from a mention node and its position within the
 * parent's content. Returns null when the node has no usable id.
 */
function buildMentionInfo(
	node: ADFEntity,
	siblings: Array<ADFEntity | undefined>,
	index: number,
	adf: ADFEntity,
): AgentMentionInfo | null {
	if (!node.attrs?.id) {
		return null;
	}

	const agentId = node.attrs.id as string;
	const rawMentionText = (node.attrs.text as string) ?? '';
	const agentName = rawMentionText.replace(/^@/, '');

	// Preserve the original single-paragraph prompt while the inline comment
	// experience is off.
	const paragraphPrompt =
		siblings
			.map((sibling, j) => {
				if (j === index) {
					return rawMentionText;
				}
				return sibling?.type === 'text' && sibling.text ? sibling.text : '';
			})
			.join('')
			.trim() || rawMentionText;
	const promptText = expValEquals('platform_editor_inline_agentic_comments', 'isEnabled', true)
		? (fg('platform_editor_gfm_comment_prompt_serialisation')
				? serializeGfmAdfText(adf)
				: serializeAdfText(adf)
			).trim() || rawMentionText
		: paragraphPrompt;

	return { agentId, agentName, promptText };
}

function serializeAdfText(adf: ADFEntity): string {
	const parts: string[] = [];
	const appendBlockBoundary = () => {
		if (parts.length > 0 && !parts[parts.length - 1].endsWith('\n')) {
			parts.push('\n');
		}
	};

	traverse(adf, {
		paragraph: appendBlockBoundary,
		heading: appendBlockBoundary,
		codeBlock: appendBlockBoundary,
		text: (node: ADFEntity) => {
			parts.push(node.text ?? '');
		},
		mention: (node: ADFEntity) => {
			parts.push((node.attrs?.text as string) ?? '');
		},
		hardBreak: () => {
			parts.push('\n');
		},
	});

	return parts.join('');
}

function serializeGfmAdfText(adf: ADFEntity): string {
	const normalized = normalizePromptAdf(adf);
	try {
		gfmMarkdownTransformer ??= createGfmMarkdownTransformer(defaultSchema);
		return gfmMarkdownTransformer.encode(defaultSchema.nodeFromJSON(normalized as JSONDocNode));
	} catch {
		return serializeAdfText(normalized);
	}
}

function normalizePromptAdf(node: ADFEntity): ADFEntity {
	if (node.type === 'mention' || node.type === 'emoji') {
		const text =
			node.type === 'mention' ? node.attrs?.text : (node.attrs?.text ?? node.attrs?.shortName);
		if (typeof text === 'string' && text) {
			return { type: 'text', text };
		}
	}
	if (node.type === 'status' && typeof node.attrs?.text === 'string') {
		return { type: 'text', text: node.attrs.text };
	}
	if (node.type === 'date' && typeof node.attrs?.timestamp === 'string') {
		const date = new Date(Number(node.attrs.timestamp));
		if (!Number.isNaN(date.getTime())) {
			return { type: 'text', text: date.toISOString().slice(0, 10) };
		}
	}
	if (node.type === 'mediaInline') {
		const name = node.attrs?.alt || node.attrs?.__fileName;
		return {
			type: 'text',
			text: typeof name === 'string' && name ? `[Attachment: ${name}]` : '[Attachment]',
		};
	}

	return Array.isArray(node.content)
		? {
				...node,
				content: node.content.flatMap((child) => (child ? [normalizePromptAdf(child)] : [])),
			}
		: node;
}

/**
 * Walks an ADF tree and returns the first APP-typed mention — the fast path for
 * LIVE (not-yet-persisted) comments, where `userType` is still present.
 *
 * For persisted/copy-pasted comments the backend strips `userType`, so this
 * returns null; `handleAgentMentionInComment` then falls back to id-resolution
 * via `extractMentionCandidatesFromAdf`.
 */
export function extractAgentMentionFromAdf(adf: AdfDocument | undefined): AgentMentionInfo | null {
	if (!adf?.content) {
		return null;
	}

	let result: AgentMentionInfo | null = null;

	// traverse walks the full tree even after we find a match — the early-return
	// guard below skips processing but doesn't stop traversal. This is acceptable
	// for comment ADF which is typically small (a few paragraphs).
	traverse(adf as ADFEntity, {
		mention: (node: ADFEntity, parent, index) => {
			if (result) {
				return; // already found one
			}

			if (node.attrs?.userType !== 'APP') {
				return;
			}

			result = buildMentionInfo(node, parent.node?.content ?? [], index, adf as ADFEntity);
		},
	});

	return result;
}

/**
 * Walks an ADF tree and returns EVERY mention (regardless of `userType`) as a
 * candidate. Used by the save path to detect agent mentions by resolving each
 * mention id against the Rovo agents endpoint — necessary because the backend
 * strips `userType` on persistence, so copy-pasted/reloaded agent mentions can
 * only be identified by their (surviving) `id`.
 */
export function extractMentionCandidatesFromAdf(adf: AdfDocument | undefined): AgentMentionInfo[] {
	if (!adf?.content) {
		return [];
	}

	const candidates: AgentMentionInfo[] = [];

	traverse(adf as ADFEntity, {
		mention: (node: ADFEntity, parent, index) => {
			const info = buildMentionInfo(node, parent.node?.content ?? [], index, adf as ADFEntity);
			if (info) {
				candidates.push(info);
			}
		},
	});

	return candidates;
}

/**
 * Collects the ids of mentions whose `userType` attribute is ABSENT — i.e.
 * persisted / copy-pasted mentions that have lost their type and therefore need
 * id-resolution to determine whether they are agents.
 *
 * Live mentions always carry a `userType`: APP agents are detected synchronously
 * by `extractAgentMentionFromAdf`, and any other type (DEFAULT/TEAM/SPECIAL) is
 * not an agent — so those are skipped here to avoid unnecessary agents-endpoint
 * calls (e.g. on every keystroke while a normal user mention is in the editor).
 */
export function extractUnresolvedMentionIds(adf: AdfDocument | undefined): string[] {
	if (!adf?.content) {
		return [];
	}

	const ids: string[] = [];

	traverse(adf as ADFEntity, {
		mention: (node: ADFEntity) => {
			const id = node.attrs?.id;
			if (typeof id === 'string' && id !== '' && !node.attrs?.userType) {
				ids.push(id);
			}
		},
	});

	return ids;
}

/**
 * Resolves an Atlassian account ID (from the mention node) to an Agent Studio
 * UUID (needed by the CwR sidebar for agent pre-selection).
 *
 * Uses the Rovo assistance API with the same pattern as AssistanceServiceImpl.
 * Returns null when the account is not an agent (the endpoint yields an error
 * body with no `id`) or on any error — so callers can also use this as an
 * "is this mention an agent?" check. Agent pre-selection degrades gracefully.
 */
async function resolveAgentStudioId(
	accountId: string,
	cloudId: string,
	signal?: AbortSignal,
): Promise<ResolvedAgent | null> {
	try {
		const response = await cfetch(
			`/gateway/api/assist/rovo/v1/agents/accountid/${normalizeIdentityAccountId(accountId)}`,
			{
				method: 'GET',
				headers: {
					'x-product': 'confluence',
					'x-experience-id': 'ai-mate',
					'x-cloudid': cloudId,
				},
				signal,
			},
		);
		const agent = await response.json();

		// A non-agent account id yields an error body with no `id`. Returning null
		// here lets callers treat this function as an "is this an agent?" check.
		if (!agent?.id) {
			return null;
		}

		return {
			id: agent.id,
			namedId: agent.named_id,
			name: agent.name,
			creatorType: agent.creator_type,
			externalConfigReference: agent.external_config_reference,
		};
	} catch (err: unknown) {
		return null;
	}
}

/**
 * Agent identity for the FYI nudge — `id` is the mention's account id (used as
 * the avatar identity) and `name` is the resolved agent display name.
 */
export type AgentMentionIdentity = {
	id: string;
	name: string;
	/**
	 * OOTB agent's fixed-avatar key (external_config_reference, falling back to named_id). Drives
	 * AgentAvatar's `agentNamedId` so out-of-the-box agents (e.g. Canva) render their real avatar
	 * instead of the deterministic fallback generated from the account id.
	 */
	agentNamedId?: string;
};

/**
 * Resolves a single mention's account id against the Rovo agents endpoint to
 * determine whether it is an agent. Returns the agent identity (`id` echoes the
 * input account id, for avatar rendering; `name` is the resolved display name),
 * or null when the id is not an agent or resolution fails.
 *
 * Used by the FYI nudge to detect pasted/persisted agent mentions that have lost
 * their `userType`. Callers MUST cache the result per id to avoid re-fetching on
 * every editor change — see `useAgentMentionFyiNudge`.
 */
export async function resolveAgentFromMentionId(
	accountId: string,
	cloudId: string,
	signal?: AbortSignal,
): Promise<AgentMentionIdentity | null> {
	const resolved = await resolveAgentStudioId(accountId, cloudId, signal);
	return resolved
		? {
				id: accountId,
				name: resolved.name,
				agentNamedId: resolved.externalConfigReference ?? resolved.namedId,
			}
		: null;
}

function normalizeIdentityAccountId(accountId: string): string {
	try {
		return IdentityUserAri.parse(accountId).userId;
	} catch {
		return accountId;
	}
}

async function setAgentSessionsProperty(pageId: string, userId: string): Promise<void> {
	const propertyKey = `cc-agent-mention-sessions:${userId}`;
	try {
		await cfetch(`/wiki/api/v2/pages/${pageId}/properties`, {
			method: 'POST',
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify({ key: propertyKey, value: true }),
		});
	} catch {
		// Best-effort write — 409 means already set; any other error is non-fatal.
	}
}

/**
 * Resolves the given mention candidates against the Rovo agents endpoint and
 * returns the first one that is a real agent, together with its Studio id.
 * Returns null when none of the mentions resolve to an agent.
 *
 * Candidates are resolved sequentially so we short-circuit on the first agent
 * (comments typically contain a single mention).
 */
async function detectAgentMention(
	candidates: AgentMentionInfo[],
	cloudId: string,
): Promise<{ mention: AgentMentionInfo; agent: ResolvedAgent } | null> {
	for (const mention of candidates) {
		const controller = new AbortController();
		const timeoutId = setTimeout(() => controller.abort(), AGENT_RESOLVE_TIMEOUT_MS);
		const resolved = await resolveAgentStudioId(mention.agentId, cloudId, controller.signal);
		clearTimeout(timeoutId);
		if (resolved?.id) {
			return { mention, agent: resolved };
		}
	}
	return null;
}

/**
 * Opens the CwR sidebar for an agent mention and fires the `chat-new` postMessage
 * (with page/comment conversation tags). When `preResolvedAgent` is provided it
 * is used directly; otherwise agent details are resolved best-effort before sending.
 */
function openSidebarAndChat(
	agentMention: AgentMentionInfo,
	cloudId: string,
	pageId: string,
	commentId: string,
	userId: string | undefined,
	commentType: ConfluenceCommentType,
	preResolvedAgent: ResolvedAgent | null,
	createAnalyticsEvent?: CreateUIAnalyticsEvent,
	presenceContext?: AgentMentionPresenceContext,
): void {
	if (expValEquals('platform_editor_inline_agentic_comments', 'isEnabled', true)) {
		// Claim before dispatch: two save callbacks would send distinct bridge commands.
		// Having a single command receiver does not deduplicate separate invocations.
		if (!claimCommentInvocation(commentId)) {
			return;
		}

		dispatchSidebarAndChat(
			agentMention,
			cloudId,
			pageId,
			commentId,
			userId,
			commentType,
			preResolvedAgent,
			true,
			createAnalyticsEvent,
			presenceContext,
		);
		return;
	}

	dispatchSidebarAndChat(
		agentMention,
		cloudId,
		pageId,
		commentId,
		userId,
		commentType,
		preResolvedAgent,
		false,
		createAnalyticsEvent,
		presenceContext,
	);
}

function dispatchSidebarAndChat(
	agentMention: AgentMentionInfo,
	cloudId: string,
	pageId: string,
	commentId: string,
	userId: string | undefined,
	commentType: ConfluenceCommentType,
	preResolvedAgent: ResolvedAgent | null,
	runHeadlessly: boolean,
	createAnalyticsEvent?: CreateUIAnalyticsEvent,
	presenceContext?: AgentMentionPresenceContext,
): void {
	if (userId && pageId) {
		void setAgentSessionsProperty(pageId, userId);
	}

	if (runHeadlessly) {
		startOptimisticCommentAgentSession(
			commentId,
			{
				agentId: agentMention.agentId,
				agentName: agentMention.agentName,
			},
			createAnalyticsEvent,
		);
	}

	// 1. Notify the sidebar and session-discovery consumers. The rollout treatment keeps the
	// sidebar closed; the existing listener still owns the visible-chat control path.
	window.dispatchEvent(
		new CustomEvent(AGENT_MENTION_COMMENT_SAVED_EVENT, {
			detail: { ...agentMention, commentId, openSidebar: !runHeadlessly },
		}),
	);

	if (
		commentType === 'inline' &&
		cloudId &&
		pageId &&
		createAnalyticsEvent &&
		presenceContext?.contentType === 'page' &&
		presenceContext.pageContext === 'confluence-page-view' &&
		expValEquals('trigger_agentic_view_on_inline_comment_mention', 'isEnabled', true)
	) {
		void recordAgenticView(
			{
				contentId: pageId,
				pageContext: presenceContext.pageContext,
				agentIdentityAccountId: normalizeIdentityAccountId(agentMention.agentId),
			},
			cloudId,
			createAnalyticsEvent,
			{ trigger: 'inlineCommentMention' },
		);
	}

	// 2. Resolve the agent, then hand off to the background command or the visible Chat path.
	const prompt = agentMention.promptText;
	const chatSourceId = `agent-mention-${agentMention.agentId}-${Date.now()}`;

	// Build the comment-mention conversation tags so the backend can create a
	// SessionAssociationPublic record (linking this conversation to the page/comment)
	// and initialize the WORKING state. ARI-formatted IDs are used so the values
	// persisted to ERS are valid Teamwork Graph ARI references, as required by
	// conversational-ai-platform PRs 31101 and 31525. The BE extracts the ARI via
	// substringAfter("page:") / substringAfter("comment:") and stores it as
	// entityId / subEntityId on SessionAssociationPublic.
	const pageAri = pageId && cloudId ? `ari:cloud:confluence:${cloudId}:page/${pageId}` : '';
	const commentAri =
		commentId && cloudId ? `ari:cloud:confluence:${cloudId}:comment/${commentId}` : '';
	const tags =
		pageAri && commentAri
			? [
					'mention-in-comment',
					`page:${pageAri}`,
					`comment:${commentAri}`,
					`comment-type:${commentType}`,
				]
			: undefined;
	const normalizedCommentType: 'INLINE' | 'FOOTER' = commentType === 'inline' ? 'INLINE' : 'FOOTER';
	const commentMentionContext =
		pageAri && commentAri && fg('confluence-at-mention-thread-session-reuse')
			? {
					pageAri,
					commentAri,
					commentType: normalizedCommentType,
				}
			: undefined;
	const chatName = prompt.length > 30 ? `${prompt.substring(0, 30).trimEnd()}…` : prompt;

	const sendChatNew = (resolvedAgent: ResolvedAgent | null) => {
		const payload: Payload = {
			type: 'chat-new',
			source: 'agentMentionComment',
			...(runHeadlessly ? { openChat: true, consumeOnce: true } : {}),
			data: {
				...(resolvedAgent ? { agentId: resolvedAgent.id } : {}),
				sourceId: chatSourceId,
				name: chatName,
				prompt,
				dialogues: [],
				mode: { useCurrentPageContext: true },
				...(tags ? { tags } : {}),
				...(commentMentionContext ? { commentMentionContext } : {}),
			},
		};
		window.postMessage(
			{
				eventType: ROVO_POST_MESSAGE_EVENT_TYPE,
				payload,
				payloadId: crypto.randomUUID(),
			},
			window.location.origin,
		);
	};

	const invokeAgent = async (resolvedAgent: ResolvedAgent | null) => {
		if (runHeadlessly && resolvedAgent && commentId && tags) {
			const sourceId = registerBackgroundConversationSource(commentId);
			try {
				const accepted = await sendBackgroundAgentInvocation({
					agentId: resolvedAgent.id,
					agentIdentityAccountId: normalizeIdentityAccountId(agentMention.agentId),
					agentName: resolvedAgent.name,
					agentNamedId: resolvedAgent.externalConfigReference ?? resolvedAgent.namedId,
					isThirdPartyAgent: resolvedAgent.creatorType
						? isForgeAgentByCreatorType(resolvedAgent.creatorType)
						: false,
					productKey: 'confluence',
					source: 'agentMentionComment',
					prompt,
					name: chatName,
					sourceId,
					tags,
					...(commentMentionContext ? { commentMentionContext } : {}),
				});
				if (accepted) {
					return;
				}
			} catch {
				clearOptimisticCommentAgentSession(commentId);
				// Delivery may already have started execution. Open Chat, but do not resend.
				window.dispatchEvent(
					new CustomEvent(AGENT_MENTION_COMMENT_SAVED_EVENT, {
						detail: { ...agentMention, commentId, openSidebar: true },
					}),
				);
				return;
			}
		}
		if (runHeadlessly) {
			clearOptimisticCommentAgentSession(commentId);
			window.dispatchEvent(
				new CustomEvent(AGENT_MENTION_COMMENT_SAVED_EVENT, {
					detail: { ...agentMention, commentId, openSidebar: true },
				}),
			);
		}
		sendChatNew(resolvedAgent);
	};

	// Agent details already known (detected via id-resolution) or cloudId missing
	// (cannot resolve) — send immediately.
	if (preResolvedAgent || !cloudId) {
		void invokeAgent(preResolvedAgent);
		return;
	}

	const controller = new AbortController();
	const timeoutId = setTimeout(() => controller.abort(), AGENT_RESOLVE_TIMEOUT_MS);

	void resolveAgentStudioId(agentMention.agentId, cloudId, controller.signal).then((resolved) => {
		clearTimeout(timeoutId);
		void invokeAgent(resolved);
	});
}

/**
 * Main entry point — called from comment save handlers.
 *
 * Detects an agent mention in the saved comment and starts an invocation through
 * the background bridge command (treatment) or visible Chat (control), with page/comment conversation
 * tags so the backend can create a SessionAssociationPublic record linking the
 * Rovo conversation to the Confluence page and comment).
 *
 * Detection is two-stage:
 *   1. Fast path — a LIVE (not-yet-persisted) comment still carries
 *      `userType: 'APP'` on the agent mention, so it is detected synchronously.
 *   2. Fallback — persisted/copy-pasted comments lose `userType` (the backend
 *      strips it on the ADF→storage→ADF round-trip), so the agent is identified
 *      by resolving each surviving mention `id` against the Rovo agents endpoint.
 *
 * The PubSub system's `triggerLatest` queue ensures the `chat-new` event is
 * delivered even if the PubSubListener hasn't mounted yet when we publish.
 *
 * @param adf - The ADF document of the saved comment
 * @param cloudId - The Confluence cloud ID (from session data)
 * @param pageId - The Confluence page ID — used to build the `page:<pageId>` tag
 * @param commentId - The newly-created comment ID — used to build the `comment:<commentId>` tag
 * @param userId - The current user's account ID — used to set the page-level sessions property
 * @param commentType - Whether the saved comment belongs to an inline or footer comment thread
 * @param createAnalyticsEvent - Analytics factory used for inline agentic-view observability
 * @param presenceContext - Caller-owned content and page context for inline agentic-view presence
 */
export function handleAgentMentionInComment(
	adf: AdfDocument,
	cloudId: string,
	pageId: string,
	commentId: string,
	userId: string | undefined,
	commentType: ConfluenceCommentType,
	createAnalyticsEvent?: CreateUIAnalyticsEvent,
	presenceContext?: AgentMentionPresenceContext,
): void {
	// Fast path — a live comment still has userType='APP' on the agent mention.
	const fastPathMention = extractAgentMentionFromAdf(adf);
	if (fastPathMention) {
		openSidebarAndChat(
			fastPathMention,
			cloudId,
			pageId,
			commentId,
			userId,
			commentType,
			null,
			createAnalyticsEvent,
			presenceContext,
		);
		return;
	}

	// Fallback — persisted/copy-pasted comment: userType has been stripped, so
	// identify the agent by resolving the surviving mention id(s). Requires a
	// cloudId to call the agents endpoint.
	if (!cloudId) {
		return;
	}

	const candidates = extractMentionCandidatesFromAdf(adf);
	if (candidates.length === 0) {
		return;
	}

	void detectAgentMention(candidates, cloudId).then((match) => {
		if (!match) {
			return; // none of the mentions are agents
		}
		openSidebarAndChat(
			match.mention,
			cloudId,
			pageId,
			commentId,
			userId,
			commentType,
			match.agent,
			createAnalyticsEvent,
			presenceContext,
		);
	});
}

/**
 * Re-opens (resumes) an existing Rovo conversation in the CwR sidebar by its
 * conversationId. Used by the per-comment agent-session action button
 * (AvatarWithActionButton) to resume the conversation associated with a comment.
 *
 * Unlike handleAgentMentionInComment — which fires `chat-new` to START a new
 * conversation — this fires the `chat-open` trigger. For `chat-open`, `channelId`
 * is the Rovo conversationId (see rovo-platform-ui-components'
 * confluence-object-finalize-popup, which opens chat with `channelId: conversationId`).
 * The conversationId comes from `content.allCommentAgentSessions`.
 */
export function reopenCommentAgentChat(conversationId: string): void {
	if (!conversationId) {
		return;
	}

	const payload: Payload = {
		type: 'chat-open',
		source: 'agentMentionComment',
		data: {
			channelId: conversationId,
		},
	};

	window.postMessage(
		{
			eventType: ROVO_POST_MESSAGE_EVENT_TYPE,
			payload,
			payloadId: crypto.randomUUID(),
		},
		window.location.origin,
	);
}
